/**
 * LoanManagement.js
 *
 * FEATURE 6 — Loan & Advance Management
 *
 * Employee view: request a loan/advance, see own requests with EMI progress.
 * HR view: review pending requests, approve (choosing start cycle) or reject,
 *   browse full history of all loans across the company.
 *
 * Calls (loans.routes.js):
 *   POST   /loans/request
 *   GET    /loans/my
 *   GET    /loans/pending
 *   GET    /loans/all
 *   PATCH  /loans/:id/review
 *   PATCH  /loans/:id/cancel
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal,
    Form, Spinner, ProgressBar, Alert, Table,
} from 'react-bootstrap';
import {
    IndianRupee, Plus, CheckCircle, XCircle, Clock,
    RefreshCw, X, TrendingDown,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const HR_ROLES = ['super-admin', 'admin', 'hr-manager', 'account-manager'];

const STATUS_BADGE = {
    pending:   { bg: 'warning', text: 'dark',  label: 'Pending Review' },
    active:    { bg: 'primary', text: 'white', label: 'Active' },
    completed: { bg: 'success', text: 'white', label: 'Completed' },
    rejected:  { bg: 'danger',  text: 'white', label: 'Rejected' },
    cancelled: { bg: 'secondary', text: 'white', label: 'Cancelled' },
};

const fmt = (n) => '\u20B9' + parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

// ── Request Modal ───────────────────────────────────────────────────────────
const RequestModal = ({ onClose, onDone }) => {
    const [form, setForm] = useState({
        loan_type: 'advance', principal_amount: '', total_installments: '3', reason: '',
    });
    const [saving, setSaving] = useState(false);

    const emi = (parseFloat(form.principal_amount) || 0) / (parseInt(form.total_installments) || 1);

    const submit = async () => {
        if (!form.principal_amount || parseFloat(form.principal_amount) <= 0)
            return toast.error('Enter a valid amount');
        if (!form.reason?.trim() || form.reason.trim().length < 10)
            return toast.error('Please provide a reason (minimum 10 characters)');
        setSaving(true);
        try {
            await axiosInstance.post('/loans/request', form);
            toast.success('Request submitted. HR will review it shortly.');
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to submit request');
        } finally { setSaving(false); }
    };

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <IndianRupee size={18} className="me-2 text-primary"/>Request Loan / Advance
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Type *</Form.Label>
                        <div className="d-flex gap-2">
                            {[['advance', 'Salary Advance'], ['loan', 'Loan']].map(([val, label]) => (
                                <button key={val} type="button"
                                    onClick={() => setForm(p => ({ ...p, loan_type: val }))}
                                    style={{
                                        flex: 1, padding: '10px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                                        border: `1.5px solid ${form.loan_type === val ? '#4f46e5' : '#e2e8f0'}`,
                                        background: form.loan_type === val ? '#eef2ff' : '#fff',
                                        color: form.loan_type === val ? '#4f46e5' : '#94a3b8',
                                    }}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Amount (\u20B9) *</Form.Label>
                        <Form.Control type="number" min="1" placeholder="e.g. 30000"
                            value={form.principal_amount}
                            onChange={e => setForm(p => ({ ...p, principal_amount: e.target.value }))}/>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Repay over (months) *</Form.Label>
                        <Form.Select value={form.total_installments}
                            onChange={e => setForm(p => ({ ...p, total_installments: e.target.value }))}>
                            {[1, 2, 3, 6, 9, 12, 18, 24, 36].map(n => (
                                <option key={n} value={n}>{n} month{n > 1 ? 's' : ''}</option>
                            ))}
                        </Form.Select>
                    </Col>
                    {emi > 0 && (
                        <Col xs={12}>
                            <div className="p-3 rounded-3" style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
                                <div className="small text-muted">Estimated monthly EMI</div>
                                <div className="fw-bold text-success" style={{ fontSize: 20 }}>
                                    {fmt(emi)} / month
                                </div>
                            </div>
                        </Col>
                    )}
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Reason *</Form.Label>
                        <Form.Control as="textarea" rows={3} placeholder="Briefly explain why you need this (min 10 characters)…"
                            value={form.reason}
                            onChange={e => setForm(p => ({ ...p, reason: e.target.value }))}/>
                    </Col>
                </Row>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" className="flex-grow-1 fw-bold" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : 'Submit Request'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// ── Approval Modal ──────────────────────────────────────────────────────────
const ReviewModal = ({ loan, onClose, onDone }) => {
    const nextCycleDefault = (() => {
        const now = new Date();
        let m = now.getMonth() + 2, y = now.getFullYear();
        if (m > 12) { m = 1; y++; }
        return { month: m, year: y };
    })();
    const [startMonth, setStartMonth] = useState(nextCycleDefault.month);
    const [startYear,  setStartYear]  = useState(nextCycleDefault.year);
    const [rejectReason, setRejectReason] = useState('');
    const [action, setAction] = useState('approve');
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (action === 'reject' && !rejectReason.trim())
            return toast.error('Please provide a rejection reason');
        setSaving(true);
        try {
            await axiosInstance.patch(`/loans/${loan.id}/review`, {
                action,
                start_month: action === 'approve' ? startMonth : undefined,
                start_year:  action === 'approve' ? startYear  : undefined,
                rejected_reason: action === 'reject' ? rejectReason.trim() : undefined,
            });
            toast.success(`Request ${action === 'approve' ? 'approved' : 'rejected'}`);
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Action failed');
        } finally { setSaving(false); }
    };

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">Review Request</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="p-3 rounded-3 mb-3" style={{ background: '#f8fafc' }}>
                    <div className="fw-bold">{loan.employee_name}</div>
                    <div className="text-muted small">{loan.employee_code} · {loan.department_name || '—'}</div>
                    <div className="mt-2 d-flex justify-content-between small">
                        <span className="text-muted">Type</span>
                        <span className="fw-medium text-capitalize">{loan.loan_type}</span>
                    </div>
                    <div className="d-flex justify-content-between small">
                        <span className="text-muted">Amount</span>
                        <span className="fw-medium">{fmt(loan.principal_amount)}</span>
                    </div>
                    <div className="d-flex justify-content-between small">
                        <span className="text-muted">Installments</span>
                        <span className="fw-medium">{loan.total_installments} months @ {fmt(loan.monthly_emi)}/mo</span>
                    </div>
                    <div className="mt-2 small">
                        <span className="text-muted">Reason: </span>{loan.reason}
                    </div>
                </div>

                <div className="d-flex gap-2 mb-3">
                    {[['approve', '✓ Approve'], ['reject', '✗ Reject']].map(([val, label]) => (
                        <button key={val} type="button"
                            onClick={() => setAction(val)}
                            style={{
                                flex: 1, padding: '10px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                                border: `1.5px solid ${action === val ? (val === 'approve' ? '#059669' : '#dc2626') : '#e2e8f0'}`,
                                background: action === val ? (val === 'approve' ? '#f0fdf4' : '#fef2f2') : '#fff',
                                color: action === val ? (val === 'approve' ? '#059669' : '#dc2626') : '#94a3b8',
                            }}>
                            {label}
                        </button>
                    ))}
                </div>

                {action === 'approve' ? (
                    <Row className="g-2">
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">EMI start month</Form.Label>
                            <Form.Select value={startMonth} onChange={e => setStartMonth(parseInt(e.target.value))}>
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Year</Form.Label>
                            <Form.Control type="number" value={startYear}
                                onChange={e => setStartYear(parseInt(e.target.value))}/>
                        </Col>
                        <Col xs={12} className="text-muted small mt-1">
                            EMI of {fmt(loan.monthly_emi)} will be automatically deducted from payroll
                            starting this cycle, every month until repaid.
                        </Col>
                    </Row>
                ) : (
                    <Form.Control as="textarea" rows={2}
                        placeholder="Reason for rejection (required)…"
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}/>
                )}

                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant={action === 'approve' ? 'success' : 'danger'}
                        className="flex-grow-1 fw-bold" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : action === 'approve' ? 'Approve' : 'Reject'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// ── Employee loan card ───────────────────────────────────────────────────────
const LoanCard = ({ loan, onCancel }) => {
    const sb = STATUS_BADGE[loan.status] || STATUS_BADGE.pending;
    const pct = loan.total_installments
        ? Math.round((loan.installments_paid / loan.total_installments) * 100)
        : 0;
    return (
        <Card className="border-0 shadow-sm mb-3">
            <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                        <span className="fw-bold text-capitalize">{loan.loan_type}</span>
                        <span className="text-muted small ms-2">requested {fmtDate(loan.requested_at)}</span>
                    </div>
                    <Badge bg={sb.bg} text={sb.text}>{sb.label}</Badge>
                </div>
                <Row className="g-2 mb-2">
                    <Col xs={4}>
                        <div className="text-muted" style={{ fontSize: 11 }}>Principal</div>
                        <div className="fw-bold">{fmt(loan.principal_amount)}</div>
                    </Col>
                    <Col xs={4}>
                        <div className="text-muted" style={{ fontSize: 11 }}>Monthly EMI</div>
                        <div className="fw-bold">{fmt(loan.monthly_emi)}</div>
                    </Col>
                    <Col xs={4}>
                        <div className="text-muted" style={{ fontSize: 11 }}>Outstanding</div>
                        <div className="fw-bold text-danger">{fmt(loan.outstanding_balance)}</div>
                    </Col>
                </Row>
                {loan.status === 'active' && (
                    <div className="mb-2">
                        <div className="d-flex justify-content-between small mb-1">
                            <span className="text-muted">{loan.installments_paid}/{loan.total_installments} paid</span>
                            <span className="text-muted">{pct}%</span>
                        </div>
                        <ProgressBar now={pct} variant="success" style={{ height: 6 }}/>
                    </div>
                )}
                {loan.status === 'rejected' && loan.rejected_reason && (
                    <Alert variant="danger" className="small py-2 mb-2">{loan.rejected_reason}</Alert>
                )}
                {loan.status === 'pending' && (
                    <Button size="sm" variant="outline-danger" onClick={() => onCancel(loan.id)}>
                        <X size={13} className="me-1"/>Cancel Request
                    </Button>
                )}
            </Card.Body>
        </Card>
    );
};

// ════════════════════════════════════════════════════════════════════════════
const LoanManagement = () => {
    const { user } = useAuth();
    const isHR = HR_ROLES.includes(user?.role);
    const [tab, setTab] = useState(isHR ? 'pending' : 'my');
    const [myLoans, setMyLoans] = useState([]);
    const [pending, setPending] = useState([]);
    const [allLoans, setAllLoans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showRequest, setShowRequest] = useState(false);
    const [reviewing, setReviewing] = useState(null);

    const loadMy = useCallback(() => {
        axiosInstance.get('/loans/my').then(r => setMyLoans(r.data.data || [])).catch(() => {});
    }, []);
    const loadPending = useCallback(() => {
        axiosInstance.get('/loans/pending').then(r => setPending(r.data.data || [])).catch(() => {});
    }, []);
    const loadAll = useCallback(() => {
        axiosInstance.get('/loans/all').then(r => setAllLoans(r.data.data || [])).catch(() => {});
    }, []);

    const loadAllData = useCallback(async () => {
        setLoading(true);
        try {
            if (isHR) await Promise.all([loadPending(), loadAll()]);
            else       await loadMy();
        } finally { setLoading(false); }
    }, [isHR, loadPending, loadAll, loadMy]);

    useEffect(() => { loadAllData(); }, [loadAllData]);

    const cancelLoan = async (id) => {
        try {
            await axiosInstance.patch(`/loans/${id}/cancel`);
            toast.success('Request cancelled');
            loadMy();
        } catch (err) { toast.error(err.response?.data?.message || 'Cancel failed'); }
    };

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <IndianRupee size={22} className="text-primary"/>Loans &amp; Advances
                    </h4>
                    <p className="text-muted small mb-0">
                        {isHR ? 'Review and manage employee loan/advance requests' : 'Request a salary advance or personal loan'}
                    </p>
                </div>
                <div className="d-flex gap-2">
                    {!isHR && (
                        <Button size="sm" variant="primary" onClick={() => setShowRequest(true)}>
                            <Plus size={14} className="me-1"/>New Request
                        </Button>
                    )}
                    <Button size="sm" variant="outline-secondary" onClick={loadAllData}>
                        <RefreshCw size={13}/>
                    </Button>
                </div>
            </div>

            {isHR && (
                <div className="d-flex gap-2 mb-4 flex-wrap">
                    <Button size="sm" variant={tab === 'pending' ? 'primary' : 'outline-secondary'}
                        onClick={() => setTab('pending')}>
                        Pending Review
                        {pending.length > 0 && <Badge bg="light" text="dark" className="ms-1">{pending.length}</Badge>}
                    </Button>
                    <Button size="sm" variant={tab === 'all' ? 'primary' : 'outline-secondary'}
                        onClick={() => setTab('all')}>
                        All Loans
                    </Button>
                </div>
            )}

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
            ) : !isHR ? (
                // ── Employee view ──────────────────────────────────────────
                myLoans.length === 0 ? (
                    <Card className="border-0 shadow-sm p-5 text-center">
                        <IndianRupee size={40} className="text-muted mx-auto mb-3 opacity-50"/>
                        <h6 className="text-muted fw-bold">No requests yet</h6>
                        <p className="text-muted small mb-3">Need a salary advance or a loan? Submit a request.</p>
                        <Button variant="primary" onClick={() => setShowRequest(true)}>
                            <Plus size={16} className="me-1"/>Request Now
                        </Button>
                    </Card>
                ) : (
                    myLoans.map(loan => <LoanCard key={loan.id} loan={loan} onCancel={cancelLoan}/>)
                )
            ) : tab === 'pending' ? (
                // ── HR pending queue ───────────────────────────────────────
                <Card className="border-0 shadow-sm">
                    {pending.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <CheckCircle size={36} className="mb-2 opacity-30"/>
                            <p>No pending requests.</p>
                        </div>
                    ) : (
                        <Table hover responsive className="mb-0 small align-middle">
                            <thead className="bg-light">
                                <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                    <th className="ps-4">Employee</th>
                                    <th>Type</th>
                                    <th>Amount</th>
                                    <th>EMI</th>
                                    <th>Reason</th>
                                    <th className="pe-4 text-end">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pending.map(loan => (
                                    <tr key={loan.id}>
                                        <td className="ps-4">
                                            <div className="fw-medium">{loan.employee_name}</div>
                                            <div className="text-muted" style={{ fontSize: 11 }}>{loan.employee_code}</div>
                                        </td>
                                        <td><Badge bg="light" text="dark" className="text-capitalize">{loan.loan_type}</Badge></td>
                                        <td className="fw-bold">{fmt(loan.principal_amount)}</td>
                                        <td>{fmt(loan.monthly_emi)}/mo × {loan.total_installments}</td>
                                        <td style={{ maxWidth: 220 }} className="text-muted">{loan.reason}</td>
                                        <td className="pe-4 text-end">
                                            <Button size="sm" variant="outline-primary" onClick={() => setReviewing(loan)}>
                                                Review
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card>
            ) : (
                // ── HR all loans ───────────────────────────────────────────
                <Card className="border-0 shadow-sm">
                    {allLoans.length === 0 ? (
                        <div className="text-center py-5 text-muted">No loan records yet.</div>
                    ) : (
                        <Table hover responsive className="mb-0 small align-middle">
                            <thead className="bg-light">
                                <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                    <th className="ps-4">Employee</th>
                                    <th>Type</th>
                                    <th>Amount</th>
                                    <th>Progress</th>
                                    <th>Outstanding</th>
                                    <th>Status</th>
                                    <th className="pe-4">Approved By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allLoans.map(loan => {
                                    const sb = STATUS_BADGE[loan.status] || STATUS_BADGE.pending;
                                    const pct = loan.total_installments
                                        ? Math.round((loan.installments_paid / loan.total_installments) * 100) : 0;
                                    return (
                                        <tr key={loan.id}>
                                            <td className="ps-4">
                                                <div className="fw-medium">{loan.employee_name}</div>
                                                <div className="text-muted" style={{ fontSize: 11 }}>{loan.employee_code}</div>
                                            </td>
                                            <td><Badge bg="light" text="dark" className="text-capitalize">{loan.loan_type}</Badge></td>
                                            <td className="fw-bold">{fmt(loan.principal_amount)}</td>
                                            <td style={{ minWidth: 120 }}>
                                                {['active', 'completed'].includes(loan.status) ? (
                                                    <>
                                                        <ProgressBar now={pct} variant="success" style={{ height: 5 }}/>
                                                        <div className="text-muted" style={{ fontSize: 10 }}>
                                                            {loan.installments_paid}/{loan.total_installments}
                                                        </div>
                                                    </>
                                                ) : <span className="text-muted">—</span>}
                                            </td>
                                            <td className="text-danger fw-medium">{fmt(loan.outstanding_balance)}</td>
                                            <td><Badge bg={sb.bg} text={sb.text} style={{ fontSize: 10 }}>{sb.label}</Badge></td>
                                            <td className="pe-4 text-muted">{loan.approved_by_name || '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </Card>
            )}

            {showRequest && (
                <RequestModal onClose={() => setShowRequest(false)} onDone={() => { setShowRequest(false); loadMy(); }}/>
            )}
            {reviewing && (
                <ReviewModal loan={reviewing} onClose={() => setReviewing(null)}
                    onDone={() => { setReviewing(null); loadPending(); loadAll(); }}/>
            )}
        </div>
    );
};

export default LoanManagement;
